import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { cn } from "@/utils/cn.ts";
import { useTranslateReturnReason } from "@babackapp/private-api-client/client/use-translate-return-reason";
import useTranslation from "@babackapp/translations/use-translation";
import { Badge } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@babackapp/ui/components/dropdown-menu";
import { AVAILABLE_LOCALES } from "@babackapp/ui/components/translatable-field";
import {
  ArrowRight,
  CheckIcon,
  LoaderIcon,
  WandSparkles,
  XCircleIcon,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  type ReturnReasonFormValues,
  useCurrentShopLocalesAvailables,
} from "./add-return-reason-dialog.tsx";

type LocaleInfo = { value: string; label: string; flag: string };

function getLocaleInfo(locale: string): LocaleInfo {
  const entry = (AVAILABLE_LOCALES as Record<string, LocaleInfo | undefined>)[
    locale
  ];
  return entry ?? { value: locale, label: locale.toUpperCase(), flag: "" };
}

function LocaleSwitcher({
  value,
  options,
  onChange,
  disabled,
}: {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const info = getLocaleInfo(value);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger disabled={disabled}>
        <Badge variant="outline">
          {info.label} {info.flag}
        </Badge>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {options.map((locale) => {
          const option = getLocaleInfo(locale);
          return (
            <DropdownMenuItem
              key={locale}
              onSelect={() => onChange(locale)}
            >
              {option.label} {option.flag}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type Status = "idle" | "loading" | "done" | "error";

export default function TranslateReturnReasonDialog() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { data: shopLanguages } = useCurrentShopLocalesAvailables();
  const form = useFormContext<ReturnReasonFormValues>();
  const watchedValues = useWatch({ control: form.control, name: "values" });
  const values = watchedValues ?? [];
  const { mutateAsync: translate } = useTranslateReturnReason();

  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<Status>("idle");

  const availableLocales = useMemo(
    () => shopLanguages ?? [],
    [shopLanguages],
  );

  const pickDefaultSource = useCallback(() => {
    const currentValues = form.getValues("values") ?? [];
    const firstFilled = currentValues.find((v) => v.text.trim().length > 0);
    return firstFilled?.lang ?? availableLocales[0] ?? "en";
  }, [availableLocales, form]);

  const [sourceLocale, setSourceLocale] = useState(pickDefaultSource);
  const [targetLocales, setTargetLocales] = useState<string[]>(() =>
    availableLocales.filter((l) => l !== pickDefaultSource()),
  );

  const sourceText =
    values.find((v) => v.lang === sourceLocale)?.text?.trim() ?? "";

  const onOpenChange = (next: boolean) => {
    if (!next && status === "loading") return;
    setOpen(next);
    // Re-pick defaults every transition so stale state from a previous session
    // (or form edits made while the dialog was closed) doesn't leak through.
    const freshSource = pickDefaultSource();
    setSourceLocale(freshSource);
    setTargetLocales(availableLocales.filter((l) => l !== freshSource));
    if (!next) setStatus("idle");
  };

  const toggleLocale = (locale: string) => {
    setTargetLocales((prev) =>
      prev.includes(locale)
        ? prev.filter((l) => l !== locale)
        : [...prev, locale],
    );
  };

  const allSelected =
    availableLocales.length > 1 &&
    availableLocales
      .filter((l) => l !== sourceLocale)
      .every((l) => targetLocales.includes(l));

  const toggleAll = () => {
    const selectable = availableLocales.filter((l) => l !== sourceLocale);
    setTargetLocales(allSelected ? [] : selectable);
  };

  const onSourceChange = (locale: string) => {
    setSourceLocale(locale);
    setTargetLocales((prev) => prev.filter((l) => l !== locale));
  };

  const canTranslate =
    status === "idle" && targetLocales.length > 0 && sourceText.length > 0;

  const onTranslate = async () => {
    if (!canTranslate) return;
    setStatus("loading");
    try {
      const { translated } = await translate({
        shopId: shop.id,
        text: sourceText,
        sourceLocale,
        targetLocales,
      });

      const values = form.getValues("values") ?? [];
      for (const [locale, translatedText] of Object.entries(translated)) {
        const index = values.findIndex((v) => v.lang === locale);
        if (index === -1 || typeof translatedText !== "string") continue;
        form.setValue(`values.${index}.text`, translatedText, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  const targetSelectable = availableLocales.filter((l) => l !== sourceLocale);

  if (availableLocales.length < 2) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5"
        >
          <WandSparkles className="size-3.5" />
          {t("settings.reasons.translate.trigger")}
        </Button>
      </DialogTrigger>
      <DialogContent
        hideCloseButton={status === "loading"}
        aria-describedby={undefined}
      >
        <div className="flex flex-col items-center justify-center gap-4 text-center pt-6">
          <div className="bg-muted size-24 rounded-full flex items-center justify-center">
            {status === "done" ? (
              <CheckIcon className="size-10 text-green-500" />
            ) : status === "error" ? (
              <XCircleIcon className="size-10 text-destructive" />
            ) : (
              <WandSparkles
                className={cn(
                  "size-10 text-primary",
                  status === "loading" && "animate-pulse",
                )}
              />
            )}
          </div>

          <div className="flex flex-col gap-4 mt-2 items-center">
            <span className="text-md">
              {status === "done"
                ? t("settings.reasons.translate.completed")
                : status === "error"
                  ? t("settings.reasons.translate.error")
                  : status === "loading"
                    ? t("settings.reasons.translate.inProgress")
                    : (
                      <>
                        {t("settings.reasons.translate.autoTranslateFrom")}{" "}
                        <LocaleSwitcher
                          value={sourceLocale}
                          options={availableLocales}
                          onChange={onSourceChange}
                          disabled={status !== "idle"}
                        />{" "}
                        {t("settings.reasons.translate.to")}
                      </>
                    )}
            </span>

            {status === "idle" && (
              <div className="flex flex-wrap gap-2 justify-center max-w-md">
                <button
                  type="button"
                  onClick={toggleAll}
                  className={cn(
                    "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-sm transition-colors cursor-pointer hover:bg-accent border-dashed",
                    allSelected && "border-primary bg-primary/5",
                  )}
                >
                  <div
                    className={cn(
                      "size-3.5 rounded-full border-2 flex items-center justify-center transition-colors",
                      allSelected
                        ? "border-primary"
                        : "border-muted-foreground",
                    )}
                  >
                    {allSelected && (
                      <div className="size-2 rounded-full bg-primary" />
                    )}
                  </div>
                  <span>
                    {allSelected
                      ? t("settings.reasons.translate.deselectAll")
                      : t("settings.reasons.translate.selectAll")}
                  </span>
                </button>

                {targetSelectable.map((locale) => {
                  const info = getLocaleInfo(locale);
                  const isSelected = targetLocales.includes(locale);
                  return (
                    <button
                      key={locale}
                      type="button"
                      onClick={() => toggleLocale(locale)}
                      className={cn(
                        "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-sm transition-colors cursor-pointer hover:bg-accent",
                        isSelected && "border-primary bg-primary/5",
                      )}
                    >
                      <div
                        className={cn(
                          "size-3.5 rounded-full border-2 flex items-center justify-center transition-colors",
                          isSelected
                            ? "border-primary"
                            : "border-muted-foreground",
                        )}
                      >
                        {isSelected && (
                          <div className="size-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <span>{info.label}</span>
                      <span>{info.flag}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {status === "idle" && sourceText.length === 0 && (
            <p className="text-sm text-destructive max-w-sm">
              {t("settings.reasons.translate.emptySource")}
            </p>
          )}

          {status === "idle" && (
            <p className="text-sm text-muted-foreground max-w-sm">
              {t("settings.reasons.translate.disclaimer")}
            </p>
          )}

          {status === "idle" && (
            <Button
              type="button"
              size="sm"
              variant="default"
              disabled={!canTranslate}
              onClick={onTranslate}
            >
              {t("settings.reasons.translate.button")} <ArrowRight />
            </Button>
          )}

          {status === "loading" && (
            <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
          )}

          {(status === "done" || status === "error") && (
            <Button
              type="button"
              size="sm"
              variant={status === "error" ? "outline" : "default"}
              onClick={() => onOpenChange(false)}
            >
              {status === "error"
                ? t("settings.reasons.translate.close")
                : t("settings.reasons.translate.done")}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
